<template>
  <div>
    <vue-headful :title="this.setPageTitle('companyProducts' + labelKeySuffix)" />
    <loading :active="isLoading" :is-full-page="true" :color="'blue'" loader="spinner"></loading>
    <b-container class="mt-4">
      <custom-flex-header :parent-node-class="['my-4']">
        <template v-slot:left-content>
          <span class="mb-0 h4 font-weight-normal">
            {{ $t('companyProducts.title' + labelKeySuffix) }}
          </span>
          <custom-tooltip :section="'company-products'" :tooltip-text="$t('companyProducts.tooltip')"></custom-tooltip>
        </template>
      </custom-flex-header>
      <b-row class="mb-4">
        <b-col cols="4">
          <div>
            <label class="fieldLabel" for="name" v-t="'companyProducts.searchByName'"></label>
            <custom-search-bar
              :debounce="800"
              :text-value="searchBarValue"
              :placeholder="$t('companyProducts.searchByName')"
              @on-search="onSearchBarUsed"
            >
            </custom-search-bar>
          </div>
        </b-col>
        <b-col cols="3" v-if="can_ViewTestProducts">
          <div>
            <label class="fieldLabel" for="name" v-t="'companyProducts.productType' + labelKeySuffix"></label>
            <custom-multi-select
              v-model="_productTypeSelectValue"
              :options="filterSelectValues.companyProductTypes"
              :select-all-text="$t('common.selectAllMale')"
              :default-text="$t('common.allMale')"
              show-all
              show-clear-selection
            ></custom-multi-select>
          </div>
        </b-col>
        <b-col cols="3" v-if="showScoringProfileFilter">
          <div>
            <label class="fieldLabel" for="name" v-t="'companyProducts.scoringProfile'"></label>
            <custom-multi-select
              v-model="_scoringProfilesSelectValue"
              :options="_scoringProfilesOptions"
              :select-all-text="$t('common.selectAllMale')"
              :default-text="$t('common.allMale')"
              show-all
              show-clear-selection
              multiple
              use-ticks
            ></custom-multi-select>
          </div>
        </b-col>
        <b-col>
          <div>
            <b-button @click="onResetFilters" variant="link" :disabled="!hasSearched" size="sm" class="mt-4">
              <span>{{ $t('search.label.resetFilters') }} <i class="fa fa-trash small ml-1 icon-size-10"></i></span>
            </b-button>
          </div>
        </b-col>
      </b-row>
      <b-row v-if="can_ViewTestProducts" class="mb-3">
        <b-col>
          <b-button size="sm" variant="primary" @click="productsInTestHandler(null, 'import')">
            <i class="fas fa-plus mr-1"></i> {{ $t('pit.importProductsInTest') }}
          </b-button>
        </b-col>
      </b-row>
      <b-row v-if="searchResult.results.length > 0">
        <b-col>
          <div class="results-table-container">
            <results-table
              is-company-products-context
              :paginator="paginator"
              :search-result="searchResult"
              :shown-fields="shownFields"
              :sorting-changed="sortingChanged"
              @on-change-page-size="onChangePageSize"
              @on-change-page-index="onChangePageIndex"
              @products-in-test-handler="productsInTestHandler"
            />
          </div>
        </b-col>
      </b-row>
      <b-row v-else>
        <b-col>
          <no-content
            :icon-name="'exclamation-circle'"
            :icon-class="['mb-3', 'fa-3x']"
            :text="$t('common.noItemsFound')"
          ></no-content>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import CustomTooltip from '../components/Lib/CustomTooltip.vue'
import CustomFlexHeader from '../components/Lib/CustomFlexHeader.vue'
import CustomSearchBar from '../components/ScoringProfiles/CustomSearchBar.vue'
import CustomMultiSelect from '../components/Lib/CustomMultiSelect.vue'
import NoContent from '../components/Lib/NoContent.vue'
import ResultsTable from '../components/Search/ResultsTable'
import { mapGetters } from 'vuex'

export default {
  components: {
    ResultsTable,
    CustomTooltip,
    CustomFlexHeader,
    CustomSearchBar,
    CustomMultiSelect,
    NoContent,
  },
  data() {
    return {
      isLoading: false,
      paginator: {
        pageIndex: 1,
        totalItems: 0,
        pageSize: 100,
      },
      searchResult: {
        sortBy: '-referenceDate',
        results: [],
      },
      searchBarValue: null,
      filters: {
        productType: null,
        scoringProfiles: null,
      },
      scoringProfilesOptions: {},
    }
  },

  computed: {
    ...mapGetters(['can_ViewTestProducts', 'can_ViewScoringProfiles']),

    isInvestmentCertificates() {
      return this.$store.getters.getSelectedAssetClass === 'investment-certificates'
    },

    // Investment Certificates call this page "Prodotti proprietari": the IC labels live under the same keys + this suffix.
    labelKeySuffix() {
      return this.isInvestmentCertificates ? 'InvestmentCertificates' : ''
    },

    // Certificates have no "Profilo associato" column, so the scoring profile filter is hidden too.
    showScoringProfileFilter() {
      return this.can_ViewScoringProfiles && !this.isInvestmentCertificates
    },

    shownFields: function () {
      return this.isInvestmentCertificates ? this.investmentCertificatesFields : this.ibipsFields
    },

    investmentCertificatesFields() {
      const dateFormatter = (value) => (value ? this.$d(new Date(value), 'short') : this.$t('common.notAvailable'))

      return [
        {
          key: 'isFollowed',
          label: '',
        },
        {
          key: 'name',
          label: this.$t('search.resultTable.name'),
          sortable: true,
        },
        {
          key: 'isin',
          label: this.$t('search.resultTable.isin'),
          sortable: true,
          formatter: (value) => value || this.$t('common.notAvailable'),
          class: ['text-nowrap', 'text-center'],
        },
        {
          key: 'issueDate',
          label: this.$t('search.resultTable.issueDate'),
          sortable: true,
          formatter: dateFormatter,
          class: ['text-nowrap', 'text-center'],
        },
        {
          key: 'maturityDate',
          label: this.$t('search.resultTable.maturityDate'),
          sortable: true,
          formatter: dateFormatter,
          class: ['text-nowrap', 'text-center'],
        },
        {
          key: 'acepiCategory',
          label: this.$t('search.resultTable.acepiCategory'),
          sortable: true,
          formatter: (value) => {
            const key = 'search.options.AcepiCategory.' + value
            return value && this.$te(key) ? this.$t(key) : value || this.$t('common.notAvailable')
          },
          class: ['text-center'],
        },
        {
          key: 'actions',
          label: this.$t('pit.actions'),
          class: 'text-center',
        },
      ]
    },

    ibipsFields() {
      let fields = [
        {
          key: 'isFollowed',
          label: '',
        },
        {
          key: 'name',
          label: this.$t('search.resultTable.name'),
          sortable: true,
        },
        {
          key: 'type',
          label: this.$t('search.resultTable.type'),
          sortable: true,
          formatter: (value) => this.filterSelectValues.productTypes[value] || this.$t('common.notAvailable'),
          class: ['text-nowrap', 'text-center'],
        },
        {
          key: 'importDate',
          sortKey: 'importDate4CompanyProducts',
          label: this.$t('search.resultTable.importDate'),
          sortable: true,
          formatter: (value) => (value ? this.dateFormat(value) : null),
          class: ['text-nowrap', 'text-center'],
        },
        {
          key: 'referenceDate',
          label: this.$t('search.resultTable.referenceDate'),
          sortable: true,
          formatter: (value) => this.$d(new Date(value), 'short'),
          class: ['text-nowrap', 'text-center'],
        },
        {
          key: 'actions',
          label: this.$t('pit.actions'),
          class: 'text-center',
        },
      ]

      // Add associated profiles field if permission enabled
      if (this.can_ViewScoringProfiles) {
        const actions = fields.pop()

        fields.push({
          key: 'scoringProfileName',
          label: this.$t('search.resultTable.associatedProfile'),
          class: ['scoring-profiles', 'text-center'],
        })

        fields.push(actions)
      }

      return fields
    },
    hasSearched() {
      const hasProductTypeSelected = this._productTypeSelectValue && this._productTypeSelectValue.length > 0
      const hasScoringProfileSelected =
        this.showScoringProfileFilter && this._scoringProfilesSelectValue && this._scoringProfilesSelectValue.length > 0
      const searchBarUsed = this.searchBarValue && this.searchBarValue.length > 0

      return searchBarUsed || hasProductTypeSelected || hasScoringProfileSelected
    },
    _scoringProfilesOptions: {
      get() {
        return this.scoringProfilesOptions
      },
      set(newVal) {
        this.scoringProfilesOptions = newVal
      },
    },
    _scoringProfilesSelectValue: {
      get() {
        return this.filters.scoringProfiles ? this.filters.scoringProfiles : []
      },
      set(newVal) {
        this.filters.scoringProfiles = newVal
        this.search()
      },
    },
    _productTypeSelectValue: {
      get() {
        return this.filters.productType ? this.filters.productType : []
      },
      set(newVal) {
        this.filters.productType = newVal
        this.search()
      },
    },
  },

  methods: {
    sortingChanged(ctx) {
      this.searchResult.sortBy = (ctx.sortDesc ? '-' : '') + ctx.sortBy
      this.search()
    },

    onSearchBarUsed(value) {
      this.searchBarValue = value
      this.search()
    },

    search() {
      this.isLoading = true

      if (!this.search.counter) {
        this.search.counter = {}
      }

      const dataToSend = {
        Sf: this.searchBarValue,
        companyProductTypes: this._productTypeSelectValue,
        scoringProfileIds: this.showScoringProfileFilter ? this._scoringProfilesSelectValue : [],
        Sort: this.searchResult.sortBy,
        Page: this.paginator.pageIndex,
        Size: this.paginator.pageSize,
      }

      // API
      this.post('/api/company/productsSearch', dataToSend)
        .then((resp) => {
          if (Array.isArray(resp.products) && resp.products.length > 0) {
            const products = resp.products.map((el) => {
              return { ...el, importDate: el.isProductInTest ? el.importDate : null }
            })
            this.searchResult.results = products
            this.paginator.totalItems = resp.paging.totalItems
          } else {
            this.searchResult.results = []
            this.paginator.totalItems = 0
          }

          if (resp.paging.pageSize) {
            this.paginator.pageSize = resp.paging.pageSize
          }
        })
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    onChangePageSize(value) {
      this.paginator.pageSize = value
      this.paginator.pageIndex = 1 // !important;
      this.search()
    },

    onChangePageIndex(value) {
      this.paginator.pageIndex = value
      this.search()
    },

    dateFormat(value) {
      if (!value) {
        return this.$t('common.notAvailable')
      }
      const date = this.$d(new Date(value), 'short')
      const time = value.split('T')[1]
      return date + ' ' + time.replace(/:[^:]*$/, '')
    },

    productsInTestHandler(item, action) {
      if (!this.can_ViewTestProducts) return

      switch (action) {
        case 'import':
          this.$router.push({ path: '/productsInTestImport' })
          break
        case 'delete':
          this.deleteProductInTest(item)
          break
      }
    },

    deleteProductInTest(item) {
      if (!item || !item.id) {
        return this.displayToast('OKV', this.$t('pit.errorOnDelete'), 'danger')
      }
      this.$bvModal.msgBoxConfirm(this.$t('pit.confirmDelete')).then((value) => {
        if (!value) return
        this.post('/api/company/pit/delete', item.id)
          .then((resp) => {
            if (resp) {
              const tableIndex = this.searchResult.results.findIndex((row) => row.id === item.id)
              if (typeof tableIndex === 'number') {
                this.searchResult.results.splice(tableIndex, 1)
                this.displayToast('OKV', this.$t('pit.successOnDelete'), 'success')
              } else {
                this.displayToast('OKV', this.$t('pit.tableIndexNotFoundOnDelete'), 'warning')
              }
            }
          })
          .catch(async (error) => {
            // A product still used by Test di Efficienza is refused with a JSON body carrying the
            // error code and how many tests use it; any other failure has nothing more to say.
            let body = null
            try {
              body = error && error.response ? await error.response.json() : null
            } catch {
              body = null
            }

            const message =
              body && body.errorCode === 'ProductInTestIsUsedByEfficiencyTests'
                ? this.$t('pit.errorOnDeleteUsedByEfficiencyTests', { count: body.count })
                : this.$t('pit.errorOnDelete')
            this.displayToast('OKV', message, 'danger')
          })
      })
    },

    loadScoringProfiles() {
      if (!this.can_ViewScoringProfiles) {
        return
      }

      this.$store
        .dispatch('scoringProfilesCommon/loadCommonScoringProfilesList')
        .then((data) => {
          this._scoringProfilesOptions = data.scoringProfiles.map((j) => {
            return {
              value: j.scoringProfileId.toString(),
              text: j.scoringProfileName,
            }
          })
        })
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
        .finally(() => (this.isLoading = false))
    },

    displayToast(title, message, variant) {
      return this.$bvToast.toast(message, {
        title: title,
        variant: variant,
      })
    },

    onResetFilters() {
      this.searchBarValue = ''
      this._productTypeSelectValue = null
      this._scoringProfilesSelectValue = null

      this.search()
    },
  },

  created() {
    this.isLoading = true
    this.loadScoringProfiles()
    this.search()
  },
}
</script>

<style lang="scss" scoped>
.header {
  font-size: 1.5rem;
}
.results-table-container {
  min-height: 150px;
}
</style>
